import { useState, useEffect, useMemo } from 'react';
import { ZxcvbnResult } from '@zxcvbn-ts/core';
import { useForm, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';

import { useSearchParams, useNavigate } from 'react-router-dom';

import logo from '../../assets/images/logo/logo-green-xl.png';
import ArrowRightLong from '../svgs/ArrowRightLong';
import Button from '../components/Button';
import InputGroup from '../components/InputGroup';
import PasswordSuggestions from '../components/PasswordSuggestions';
import ForgotPasswordModal from '../components/Modals/ForgotPasswordModal';

import {
  useValidateRecoveryCodeQuery,
  useChangePasswordWithRecoveryCodeMutation,
} from '../../generated/graphql';

import { checkPasswordStrength } from '../lib/password-strength';
import { getPasswordErrorMessage } from '../lib/form-validators';

interface ResetPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recoveryCode = searchParams.get('recovery-code') || '';
  const isMobileSetup = searchParams.get('context') === 'mobile';

  const copy = useMemo(
    () =>
      isMobileSetup
        ? {
            title: 'Set your Homecoming Companion (mobile app) password',
            body: 'This password is for the Homecoming Companion app only. It will not change your web login.',
            submit: 'Set mobile app password',
            invalidCode: 'Invalid or expired setup link',
            retry: '',
            success:
              'Your mobile app password is set. You can now log in on the Homecoming Companion mobile app.',
          }
        : {
            title: 'Reset your password',
            body: 'Please enter a new password',
            submit: 'Reset your password',
            invalidCode: 'Invalid password recovery code',
            retry: 'Reset your password again',
            success: "You've successfully changed your password",
          },
    [isMobileSetup],
  );

  const { data: validateRecoveryCodeData } = useValidateRecoveryCodeQuery({
    variables: { recoveryCode },
  });

  useEffect(() => {
    if (!validateRecoveryCodeData?.validateRecoveryCode) {
      setResetEnabled(true);
    }
    if (validateRecoveryCodeData?.validateRecoveryCode === false) {
      setResetEnabled(false);
    }
  }, [validateRecoveryCodeData]);

  const [resetEnabled, setResetEnabled] = useState<boolean | null>(null);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);

  const [passwordStrengthResult, setPasswordStrengthResult] = useState<
    ZxcvbnResult | undefined
  >();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<ResetPasswordFormData>({
    reValidateMode: 'onChange',
  });

  const [changePasswordWithRecoveryCodeMutation] =
    useChangePasswordWithRecoveryCodeMutation();

  const onSubmit: SubmitHandler<ResetPasswordFormData> = async (data) => {
    try {
      await changePasswordWithRecoveryCodeMutation({
        variables: {
          recoveryCode,
          newPassword: data.newPassword,
        },
      });
      setResetEnabled(null);
      setPasswordResetSuccess(true);
    } catch (err) {
      setError('confirmPassword', {
        type: 'Invalid code',
        message: 'Error resetting password',
      });
    }
  };

  const handleErrors: SubmitErrorHandler<ResetPasswordFormData> = (errors) => {
    console.log('errors:', errors);
  };

  return (
    <>
      <div className="flex min-h-screen">
        <div className="flex grow flex-col items-center">
          <div className="my-24">
            <img src={logo} className="h-12 w-12" alt="logo" />
          </div>
          <div className="mb-3 font-serif text-subtitle text-green-150">
            {copy.title}
          </div>
          {resetEnabled !== null && (
            <>
              {resetEnabled ? (
                <>
                  <div className="mb-12 font-sans text-body text-green-125">
                    {copy.body}
                  </div>
                  <form
                    className="flex w-[405px] flex-col pb-24"
                    onSubmit={handleSubmit(onSubmit, handleErrors)}
                  >
                    <InputGroup
                      label="New password"
                      type="password"
                      containerClassName="mb-6"
                      errorMessage={errors.newPassword?.message}
                      passwordStrengthScore={passwordStrengthResult?.score}
                      required
                      {...register('newPassword', {
                        validate: (password) => {
                          const passwordStrengthResult =
                            checkPasswordStrength(password);
                          setPasswordStrengthResult(passwordStrengthResult);

                          return getPasswordErrorMessage(
                            passwordStrengthResult,
                          );
                        },
                      })}
                    />
                    <InputGroup
                      label="Confirm password"
                      type="password"
                      required
                      errorMessage={errors.confirmPassword?.message}
                      {...register('confirmPassword', {
                        required: true,
                        validate: (passwordValue: string) => {
                          if (watch('newPassword') !== passwordValue) {
                            return 'Your passwords do not match';
                          }
                        },
                      })}
                    />

                    <PasswordSuggestions
                      strengthResult={passwordStrengthResult}
                    />

                    <Button
                      title={copy.submit}
                      IconComponent={ArrowRightLong}
                      className="mt-10"
                      type="submit"
                    />
                  </form>
                </>
              ) : (
                <>
                  <span className="text-extra-small text-red-150">
                    {copy.invalidCode}
                  </span>
                  {isMobileSetup ? (
                    <div className="mt-10 text-center text-small text-green-125">
                      Contact{' '}
                      <a
                        href="mailto:support@homecoming.health"
                        className="font-medium text-green-100 hover:underline"
                      >
                        support@homecoming.health
                      </a>{' '}
                      to request a new setup link.
                    </div>
                  ) : (
                    <Button
                      title={copy.retry}
                      className="mt-10"
                      IconComponent={ArrowRightLong}
                      onClick={() => setIsForgotPasswordModalOpen(true)}
                    />
                  )}
                </>
              )}
            </>
          )}
          {passwordResetSuccess && (
            <>
              <span className="max-w-md text-center text-small text-green-150">
                {copy.success}
              </span>
              {!isMobileSetup && (
                <Button
                  title={'Back to login'}
                  className="mt-10"
                  IconComponent={ArrowRightLong}
                  onClick={() => navigate('/login')}
                />
              )}
            </>
          )}
        </div>
        <div>
          <div className="hidden h-full w-[426px] shrink bg-[url('assets/images/login-side-bg.png')] bg-cover lg:block" />
        </div>
      </div>
      <ForgotPasswordModal
        isModalOpen={isForgotPasswordModalOpen}
        setClosed={() => setIsForgotPasswordModalOpen(false)}
      />
    </>
  );
};

export default ResetPassword;
